import React, { useState, useEffect } from 'react';
import { 
  History, ChevronDown, ChevronUp, CheckCircle, 
  Calendar, Download, FileText, 
  Loader, FolderOpen, Clock, Paperclip
} from 'lucide-react';
import { getClientProjects } from '../../services/projectService';
import { getProjectMilestones } from '../../services/milestoneService';
import { getProjectFiles, formatFileSize } from '../../services/storageService';
import FilesModal from '../common/FilesModal';
import BudgetModal from './BudgetModal';
import './ProjectHistory.css';

const ProjectHistory = ({ currentUser }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProject, setExpandedProject] = useState(null);
  const [projectDetails, setProjectDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});
  const [filesModal, setFilesModal] = useState(null);
  const [expandedMilestone, setExpandedMilestone] = useState(null);
  const [budgetModal, setBudgetModal] = useState(null); // { project, milestones }

  const toggleMilestone = (id) => setExpandedMilestone(prev => prev === id ? null : id);

  useEffect(() => {
    if (!currentUser?.uid) return;
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const loadHistory = async () => {
    try {
      const all = await getClientProjects(currentUser.uid);
      const history = all.filter(p => p.status === 'completed' || p.status === 'paid');
      history.sort((a, b) => (b.completedAt?.seconds || 0) - (a.completedAt?.seconds || 0));
      setProjects(history);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleProject = async (projectId) => {
    if (expandedProject === projectId) {
      setExpandedProject(null);
      return;
    }
    setExpandedProject(projectId);
    if (!projectDetails[projectId]) {
      setLoadingDetails(prev => ({ ...prev, [projectId]: true }));
      try {
        const [milestones, files] = await Promise.all([
          getProjectMilestones(projectId),
          getProjectFiles(projectId, currentUser.uid)
        ]);
        setProjectDetails(prev => ({ ...prev, [projectId]: { milestones, files } }));
      } catch (error) {
        console.error('Error loading project details:', error);
      } finally {
        setLoadingDetails(prev => ({ ...prev, [projectId]: false }));
      }
    }
  };

  const getMilestoneFiles = (projectId, milestoneId) =>
    projectDetails[projectId]?.files?.filter(f => f.milestoneId === milestoneId) || [];

  const getUnassignedFiles = (projectId) =>
    projectDetails[projectId]?.files?.filter(f => !f.milestoneId) || [];

  const handleViewMilestoneFiles = (milestoneId, milestoneTitle, projectId) => {
    setFilesModal({ title: `Files — ${milestoneTitle}`, files: getMilestoneFiles(projectId, milestoneId) });
  };

  const handleViewAllFiles = (projectId, projectName) => {
    setFilesModal({ title: `All files — ${projectName}`, files: projectDetails[projectId]?.files || [] });
  };

  const handleDownload = async (url, fileName) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      window.open(url, '_blank');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    return timestamp.toDate().toLocaleDateString('es-ES', {
      day: '2-digit', month: 'long', year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '—';
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const getCategoryLabel = (category) => {
    const labels = { draft: '📝 Draft', final: '✅ Final', client: '👤 Mine' };
    return labels[category] || category;
  };

  const getMilestoneStatusIcon = (status) => {
    if (status === 'completed') return <CheckCircle size={14} className="status-icon completed" />;
    if (status === 'in_progress') return <Clock size={14} className="status-icon in-progress" />;
    return <Clock size={14} className="status-icon pending" />;
  };

  if (loading) {
    return (
      <div className="history-loading">
        <Loader className="spinner" size={32} />
        <p>Cargando historial...</p>
      </div>
    );
  }

  return (
    <div className="project-history">
      <div className="history-header">
        <div>
          <h1><History size={24} />Historial de Proyectos</h1>
          <p>Todos tus proyectos completados y su documentación</p>
        </div>
        <div className="history-count">
          {projects.length} {projects.length === 1 ? 'proyecto' : 'proyectos'}
        </div>
      </div>

      {projects.length === 0 ? (
        <div className="history-empty">
          <FolderOpen size={56} />
          <h3>Sin historial aún</h3>
          <p>Aquí aparecerán tus proyectos completados</p>
        </div>
      ) : (
        <div className="history-list">
          {projects.map(project => {
            const isExpanded = expandedProject === project.id;
            const details = projectDetails[project.id];
            const isLoadingDetails = loadingDetails[project.id];
            const completedMilestones = details?.milestones?.filter(m => m.status === 'completed').length || (project.completedMilestones || 0);
            const totalMilestones = details?.milestones?.length || (project.totalMilestones || 0);
            const unassigned = getUnassignedFiles(project.id);
            const allFiles = details?.files || [];

            return (
              <div key={project.id} className={`history-card ${isExpanded ? 'expanded' : ''}`} style={{width:'100%',boxSizing:'border-box',minWidth:0}}>
                <div className="history-card-header" onClick={() => toggleProject(project.id)}>
                  <div className="history-card-main">
                    <div className="history-card-title">
                      <h3>{project.name}</h3>
                      <span className={`status-badge ${project.status}`}>
                        {project.status === 'paid' ? '💳 Pagado' : '✅ Completado'}
                      </span>
                    </div>
                    <div className="history-card-meta">
                      {(project.completedAt || project.paidAt) && (
                        <span className="meta-item">
                          <Calendar size={14} />
                          {formatDate(project.paidAt || project.completedAt)}
                        </span>
                      )}
                      {project.budget && (
                        <span className="meta-item budget">
                          
                          {formatCurrency(project.budget)}
                        </span>
                      )}
                      <button
                        className="btn-budget-label"
                        onClick={e => { e.stopPropagation(); setBudgetModal({ project, milestones: projectDetails[project.id]?.milestones || [] }); }}
                      >
                        Budget
                      </button>
                      {totalMilestones > 0 && (
                        <span className="meta-item progress-meta">
                          <span className="progress-bar-mini">
                            <span className="progress-fill-mini" style={{width:`${Math.round((completedMilestones/totalMilestones)*100)}%`}} />
                          </span>
                          {completedMilestones}/{totalMilestones}
                        </span>
                      )}
                    </div>
                  </div>
                  <button className="expand-btn">
                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                </div>

                {isExpanded && (
                  <div className="history-card-body" style={{width:'100%',boxSizing:'border-box',overflow:'hidden'}}>
                    {isLoadingDetails ? (
                      <div className="details-loading">
                        <Loader className="spinner" size={20} />
                        <p>Cargando detalles...</p>
                      </div>
                    ) : (
                      <>
                        {/* Description */}
                        {project.description && (
                          <div className="details-section description-section">
                            <p className="project-description-text">{project.description}</p>
                          </div>
                        )}

                        {/* Milestones */}
                        <div className="details-section">
                          <h4>
                            <CheckCircle size={16} />
                            Milestones
                            {details?.milestones?.length > 0 && (
                              <span className="section-count">
                                {completedMilestones}/{totalMilestones} completados
                              </span>
                            )}
                          </h4>
                          {!details?.milestones?.length ? (
                            <p className="details-empty">Sin milestones registrados</p>
                          ) : (
                            <div className="milestones-list">
                              {details.milestones.map(milestone => {
                                const mFiles = getMilestoneFiles(project.id, milestone.id);
                                return (
                                  <div key={milestone.id} className={`milestone-row ${milestone.status}`}>
                                    <div className="milestone-row-top" onClick={() => milestone.description && toggleMilestone(milestone.id)}>
                                      <div className="milestone-row-left">
                                        {getMilestoneStatusIcon(milestone.status)}
                                        <span className="milestone-title">{milestone.title}</span>
                                        {milestone.description && (
                                          <span className="milestone-accordion-icon">
                                            {expandedMilestone === milestone.id ? '▲' : '▼'}
                                          </span>
                                        )}
                                      </div>
                                      <div className="milestone-row-meta">
                                        {milestone.completedAt && (
                                          <span className="milestone-date">{formatDate(milestone.completedAt)}</span>
                                        )}
                                      </div>
                                    </div>
                                    {milestone.description && expandedMilestone === milestone.id && (
                                      <div className="milestone-desc-body">
                                        <p>{milestone.description}</p>
                                        {mFiles.length > 0 && (
                                          <button
                                            className="milestone-files-btn"
                                            onClick={(e) => { e.stopPropagation(); handleViewMilestoneFiles(milestone.id, milestone.title, project.id); }}
                                          >
                                            <Paperclip size={13} />
                                            Files ({mFiles.length})
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Files */}
                        <div className="details-section">
                          <h4>
                            <FileText size={16} />
                            Archivos del proyecto
                            {allFiles.length > 0 && (
                              <span className="section-count">{allFiles.length} archivos</span>
                            )}
                          </h4>
                          {!allFiles.length ? (
                            <p className="details-empty">Sin archivos en este proyecto</p>
                          ) : unassigned.length === 0 ? (
                            <div className="details-empty">
                              <p>Todos los archivos están asociados a milestones</p>
                              <button
                                className="view-all-files-btn"
                                onClick={() => handleViewAllFiles(project.id, project.name)}
                              >
                                <Paperclip size={14} />
                                Ver todos los archivos ({allFiles.length})
                              </button>
                            </div>
                          ) : (
                            <div className="files-list">
                              {unassigned.map(file => (
                                <div key={file.id} className="file-row">
                                  <div className="file-row-info">
                                    <span className="file-row-name">{file.fileName}</span>
                                    <div className="file-row-meta">
                                      <span className="file-category-badge">
                                        {getCategoryLabel(file.category)}
                                      </span>
                                      <span className="file-row-size">{formatFileSize(file.fileSize)}</span>
                                      <span className="file-row-date">{formatDate(file.uploadedAt)}</span>
                                    </div>
                                  </div>
                                  <button
                                    className="download-btn"
                                    onClick={() => handleDownload(file.downloadURL, file.fileName)}
                                    title="Descargar"
                                  >
                                    <Download size={16} />
                                  </button>
                                </div>
                              ))}
                              {allFiles.length > unassigned.length && (
                                <button
                                  className="view-all-files-btn"
                                  onClick={() => handleViewAllFiles(project.id, project.name)}
                                >
                                  <Paperclip size={14} />
                                  Ver todos los archivos ({allFiles.length})
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {filesModal && (
        <FilesModal
          title={filesModal.title}
          files={filesModal.files}
          onClose={() => setFilesModal(null)}
        />
      )}

      {budgetModal && (
        <BudgetModal
          project={budgetModal.project}
          milestones={budgetModal.milestones}
          currentUser={currentUser}
          isAdmin={false}
          onClose={() => setBudgetModal(null)}
        />
      )}
    </div>
  );
};

export default ProjectHistory;